import { useEffect } from 'react';

import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { NextPage } from 'next';

import { hotjar } from 'react-hotjar';
import { useRecoilState } from 'recoil';
import { v4 as uuid } from 'uuid';

import Video from '@components_iqfu/next_japan/video/video';
import LandHouseCard from '@components_iqfu/public/check_price/landhouse_card/LandHouseCard';
import MansionCard from '@components_iqfu/public/check_price/mansion_card/MansionCard';
import Footer from '@components_iqfu/public/footer/Footer';

import userUuidValue from '@recoil_iqfu/atoms/public/user_uuid';

import styles from '@styles_iqfu/public/next-japan.module.scss';

const NEXT_JAPAN: NextPage = ({}) => {
  const metaTitle =
    '簡単で素早い査定と高値での不動産売却・買取を実現。地元で不動産売却に強い会社をランキング形式で比較|不動産売却・買取専門のイクラ不動産';

  const metaDescription =
    '「どこよりも簡単で素早い査定」と「自分に合った、高値での不動産売却・買取」を実現する不動産売却専門サイト、「イクラ不動産」ページです。不動産の売却相場・価格の調査や査定が素早く・簡単にできるのはもちろん、弊社で審査して厳選した、全国各地で地元の「土地・（中古）マンション・（中古）一戸建て」の売却・買取・査定に強い、おすすめの加盟不動産会社をランキング形式で比較検討できます。また、「一戸建ての売却が得意な不動産屋さん」「マンションの売却に強い不動産屋さん」といった「強み別」の不動産会社ランキングもあるので、自分に合った切り口で不動産会社を比較・検討できます。併せて、全国の不動産売却市場・相場の動向や、売却に必要な知識から実際にあった売却事例まで、不動産売却をするにあたって必要な情報も掲載しています。';

  const metaOGUrl = `${process.env.NEXT_PUBLIC_CLIENT_BASEPATH}/press/next-japan`;

  const router = useRouter();

  const [userUuid, setUserUuid] = useRecoilState(userUuidValue);
  useEffect(() => {
    if (router.isReady) {
      if (!userUuid) {
        setUserUuid(uuid());
      }
    }
  }, [router]);

  //Hotjarで計測
  useEffect(() => {
    if (
      process.env.NEXT_PUBLIC_APP_ENV === 'production_server' ||
      process.env.NEXT_PUBLIC_APP_ENV === 'development_server'
    ) {
      setTimeout(() => {
        hotjar.initialize(Number(process.env.NEXT_PUBLIC_HOTJAR_ID), Number(process.env.NEXT_PUBLIC_HOTJAR_SV));
      }, 500); // Delay of 0.5 seconds (500 milliseconds)
    }
  }, []);

  return (
    <>
      <>
        <Head>
          <title key="title">{metaTitle}</title>
          <meta name="description" content={metaDescription} key="description" />
          <meta property="og:title" content={metaTitle} key={'og_title'} />
          <meta property="og:description" content={metaDescription} key={'og_description'} />
          <meta property="og:url" content={metaOGUrl} key={'og_url'} />
          <meta name="twitter:title" content={metaTitle} key={'twitter_title'} />
          <meta name="twitter:description" content={metaDescription} key={'twitter_description'} />
          <link rel="canonical" href={metaOGUrl} />
          <meta name="robots" content="noindex" />
        </Head>
        <div className={styles.container}>
          <div className={styles.top_bg}>
            <div className={styles.radial_gradation_layer} />
            <img className={styles.sun_layer} src="/images/iqfu/next_japan/hero-sun.svg" alt="" />
            <img className={styles.stars_left_layer} src="/images/iqfu/next_japan/hero_stars_left.svg" alt="" />
            <img className={styles.stars_right_layer} src="/images/iqfu/next_japan/hero_stars_right.svg" alt="" />
            <img className={styles.screen_layer_1} src="/images/iqfu/next_japan/hero_screen_layer_1.png" alt="" />
            <img className={styles.screen_layer_2} src="/images/iqfu/next_japan/hero_screen_layer_2.png" alt="" />
            <img className={styles.screen_layer_3} src="/images/iqfu/next_japan/hero_screen_layer_3.png" alt="" />
            <img className={styles.screen_layer_4} src="/images/iqfu/next_japan/hero_screen_layer_4.png" alt="" />
            <img className={styles.screen_layer_5} src="/images/iqfu/next_japan/hero_screen_layer_5.png" alt="" />
            <img
              className={`${styles.mobile_bg_layer} ${styles.mobile_only}`}
              src="/images/iqfu/next_japan/mobile_hero_bg.png"
              alt=""
            />
          </div>
          <div className={styles.hero_section}>
            <div className={styles.hero_section_title_desktop}>
              <h1 className={styles.page_title}>
                <img src="/images/iqfu/next_japan/hero_heading.png" alt="イクラ不動産がテレビで紹介されました" />
              </h1>
              <h2 className={styles.page_sub_title}>
                <img src="/images/iqfu/next_japan/hero_sub_heading.png" alt="MBS「NEXT JAPAN〜熱き求道者〜」" />
              </h2>
            </div>
            <div className={styles.hero_section_title_mobile}>
              <h1>
                <img
                  src="/images/iqfu/next_japan/mobile_hero.png"
                  alt="イクラ不動産がテレビで紹介されました！！ MBS「NEXT JAPAN〜熱き求道者〜」"
                />
              </h1>
            </div>
            <div className={styles.video_container}>
              <Video eventName="click_video_next_japan" />
            </div>
            <Link href="/signup" className={styles.cv_button}>
              イクラ不動産で売却をはじめる
              <img src="/images/iqfu/next_japan/click_icon.png" className={styles.click_icon} />
            </Link>
          </div>
          <div className={styles.service_intro_section}>
            <div className={styles.service_intro_section_stacks}>
              <div className={styles.service_intro_section1}>
                <div className={styles.intro_section1_decoration}>
                  <img
                    src="/images/iqfu/next_japan/service_intro_decoration_1.png"
                    alt=""
                    className={styles.desktop_only}
                  />
                  <img
                    src="/images/iqfu/next_japan/service_intro_decoration_1_mobile.png"
                    alt=""
                    className={styles.mobile_only}
                  />
                </div>
                <div className={styles.service_intro_header_container}>
                  <h3 className={styles.service_intro_header}>
                    相続や離婚などで迫られる
                    <br className={styles.mobile_only} />
                    <span className={`${styles.service_intro_header__big} ${styles.service_intro_header__orange}`}>
                      突然の不動産売却
                    </span>
                    <br />
                    はじめての経験なので戸惑う人も多いですよね
                  </h3>
                </div>
                <div className={styles.intro_screen_shot_container}>
                  <div className={styles.intro_screen_shot}>
                    <img src="/images/iqfu/next_japan/service_intro_1.webp" alt="" />
                  </div>
                </div>
              </div>
              <div className={styles.service_intro_section2}>
                <div className={styles.intro_section2_decoration}>
                  <img
                    src="/images/iqfu/next_japan/service_intro_decoration_2.png"
                    alt=""
                    className={styles.desktop_only}
                  />
                  <img
                    src="/images/iqfu/next_japan/service_intro_decoration_2_mobile.png"
                    alt=""
                    className={styles.mobile_only}
                  />
                </div>
                <div className={styles.service_intro_header_container}>
                  <h3 className={styles.service_intro_header}>
                    不動産を売りたい人が複数の不動産会社に査定を依頼しても
                    <br className={styles.desktop_only} />
                    金額がまちまちで
                    <span className={`${styles.service_intro_header__big} ${styles.service_intro_header__blue}`}>
                      適正価格がわかりにくかったりします
                    </span>
                    …
                  </h3>
                </div>
                <div className={styles.intro_screen_shot_container}>
                  <div className={styles.intro_screen_shot}>
                    <img src="/images/iqfu/next_japan/service_intro_2.webp" alt="" />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.service_intro_section3}>
              <div className={styles.intro_section3_decoration}>
                <img
                  src="/images/iqfu/next_japan/service_intro_decoration_3.png"
                  alt=""
                  className={styles.desktop_only}
                />
                <img
                  src="/images/iqfu/next_japan/service_intro_decoration_3_mobile.png"
                  alt=""
                  className={styles.mobile_only}
                />
              </div>
              <div className={styles.service_intro_header_container}>
                <h3 className={styles.service_intro_header}>
                  イクラ不動産なら、
                  <br />
                  おおまかな物件情報の入力だけで全国の不動産会社と提携して得た情報を基に
                  <br />
                  <span className={`${styles.service_intro_header__big} ${styles.service_intro_header__orange}`}>
                    実際に売れた金額や相場がすぐにわかります
                  </span>
                  <br />
                  希望しない限り、
                  <span className={`${styles.service_intro_header__big} ${styles.service_intro_header__blue}`}>
                    不動産会社から直接、営業されることもありません
                  </span>
                </h3>
              </div>
              <div className={styles.intro_screen_shot_container}>
                <div className={styles.intro_screen_shot}>
                  <img src="/images/iqfu/next_japan/service_intro_3.webp" alt="" />
                </div>
              </div>
            </div>
          </div>
          {/* end of service_intro_section */}
          <div className={styles.wavy_background_layer_container}>
            <div className={styles.wavy_background_layer}></div>
          </div>

          <div className={styles.action_area_container}>
            <div className={styles.action_area}>
              <div className={styles.action_area_left_decoration}>
                <img src="/images/iqfu/next_japan/action_decoration_left.png" alt="" />
              </div>
              <div className={styles.action_area_right_decoration}>
                <img src="/images/iqfu/next_japan/action_decoration_right.png" alt="" />
              </div>
              <div className={styles.action_area_1}>
                <h3 className={styles.search_price_title}>
                  <img src="/images/iqfu/next_japan/search_price_title.png" alt="物件の価格の検索はこちら" />
                </h3>
                <div className={styles.search_price_cards_stack}>
                  <div className={styles.search_price_card_container}>
                    <LandHouseCard pageLabel={''} />
                  </div>
                  <div className={styles.search_price_card_container}>
                    <MansionCard
                      pageLabel={''}
                      searchOption={{
                        area: 'country',
                      }}
                    />
                  </div>
                </div>
                <div className={styles.action_cv1}>
                  <p className={styles.action_cv1_lead}>または、</p>
                  <Link href="/signup_simulator" className={`${styles.cv_button} ${styles.cv_button_two_lines_mobile}`}>
                    イクラ運営スタッフに
                    <br className={styles.mobile_only} />
                    査定を依頼する
                    <img src="/images/iqfu/next_japan/click_icon.png" className={styles.click_icon} />
                  </Link>
                </div>
              </div>
              {/* end of action_area_1 */}
              <div className={styles.action_area_2}>
                <div className={styles.action_area_bg} />
                <div className={styles.action_area_screenshot}>
                  <img src="/images/iqfu/next_japan/inquiry_screenshot.webp" alt="" />
                  <div className={`${styles.ikuko_free_icon} ${styles.desktop_only}`}>
                    <img src="/images/iqfu/next_japan/free_ikuko.png" alt="" />
                  </div>
                  <div className={`${styles.real_estates_icon} ${styles.desktop_only}`}>
                    <img src="/images/iqfu/next_japan/real_estates.png" alt="" />
                  </div>
                  <div className={styles.screen_shot_right_radiowave}>
                    <img src="/images/iqfu/next_japan/service_right_radiowave.png" alt="" />
                  </div>
                </div>
                <div className={styles.action_area_description}>
                  イクラ不動産では
                  <span className={styles.action_area_description_strong}>
                    チャットで
                    <br className={styles.desktop_only} />
                    相談できるサービス
                  </span>
                  も行っています
                  <br />
                  希望に応じて不動産会社のご紹介も可能です（無料）
                </div>
                <div className={styles.action_cv_area}>
                  <Link href="/signup_inquiry" className={`${styles.cv_button} ${styles.cv_button_two_lines_mobile}`}>
                    イクラ運営スタッフに
                    <br className={styles.mobile_only} />
                    売却相談する
                    <img src="/images/iqfu/next_japan/click_icon.png" className={styles.click_icon} />
                  </Link>
                </div>
              </div>
              {/* end of action_area_2 */}
              <div className={styles.action_area_3}>
                <div className={styles.action_area_bg} />
                <div className={styles.action_area_screenshot}>
                  <img src="/images/iqfu/next_japan/big_data_screenshot.webp" alt="" />
                  <div className={`${styles.ozigi_ikuko_icon} ${styles.desktop_only}`}>
                    <img src="/images/iqfu/next_japan/ozigi_ikuko.png" alt="" />
                  </div>
                  <div className={styles.screen_shot_left_radiowave}>
                    <img src="/images/iqfu/next_japan/service_intro_left_radiowave.png" alt="" />
                  </div>
                </div>
                <div className={styles.action_area_description}>
                  正確な価格情報をご提供できるように、
                  <br className={styles.desktop_only} />
                  <span className={styles.action_area_description_strong}>ビッグデータを活用した新たな取り組み</span>
                  なども
                  <br className={styles.desktop_only} />
                  すすめております
                </div>
                <div className={styles.action_cv_area}>
                  <Link href="/signup" className={`${styles.cv_button} ${styles.cv_button_two_lines_mobile}`}>
                    イクラ不動産で
                    <br className={styles.mobile_only} />
                    売却をはじめる
                    <img src="/images/iqfu/next_japan/click_icon.png" className={styles.click_icon} />
                  </Link>
                </div>
              </div>
              {/* end of action_area_3 */}
            </div>
            {/* end of action_area */}
          </div>
          {/* end of action_area_container */}
          <div className={styles.footer_container}>
            <Footer />
          </div>
        </div>
      </>
    </>
  );
};

export default NEXT_JAPAN;
